import { v4 as uuid } from 'uuid';

import { DropdownListItem } from '@verantos-dev/shared-components';
import {
  InputFieldConfig,
  InputFieldLegacy,
  SimpleEventType,
  SimpleEventLegacy,
  DerivedEventLegacy,
  FilterLegacy,
  SimpleEventFilterType,
  InputFieldTypeLegacy,
  Attribute,
  UniquenessFilterTimeUnitEnumLegacy,
  DerivedEventFilterLegacy,
  DerivedEventFilterType,
  DerivedEventTypeLegacy,
  ActiveCohortBuilderItemLegacy,
  SubjectVariableTypeLegacy,
  SubjectVariableFilterType,
  SimpleEventTypeLegacy,
  GrammarDerivedEventTypeLegacy,
  ActiveIndexDateItemLegacy,
} from '@verantos-dev/types-cohorts';
import { ConceptGroup } from '@verantos-dev/types-concepts';

import cohortSpec, { ValueType } from '../rules';
import { VALUE_TYPE_TO_INPUT_TYPE, VALUE_TYPE_TO_INPUT_LABEL } from './inputFieldUtils';
import processConceptGroups from './processConceptGroups';
import {
  COMPONENT_COMBOBOX_MEDIUM,
  COMPONENT_DROPDOWN_NARROW,
  COMPONENT_DROPDOWN_WIDE,
  COMPONENT_RADIO,
  COMPONENT_NUMERIC_INTEGER,
  COMPONENT_COMBOBOX_NARROW,
  COMPONENT_COMBOBOX_WIDE,
} from '../../constants/constants';

export const buildConfigMap = (
  simpleEventType: SimpleEventType | SimpleEventTypeLegacy,
  filter: FilterLegacy,
  filterType: SimpleEventFilterType,
  conceptGroups: ConceptGroup[],
  edit = false
): Record<string, InputFieldConfig> => {
  let unit, entity;
  let [attr, op, value] = filter.inputFields;
  if (filterType === SimpleEventFilterType.UNIQUENESS) {
    [op, value, unit, attr] = filter.inputFields;
    if (!edit) {
      unit.value = UniquenessFilterTimeUnitEnumLegacy.DAYS;
    }
  } else if (filterType === SimpleEventFilterType.RELATED_ENTITY) {
    [entity, attr, op, value] = filter.inputFields;
  }

  // Convert SimpleEventTypeLegacy to SimpleEventType if needed
  const newSimpleEventType: SimpleEventType = simpleEventType as SimpleEventType;

  const attrOptions =
    cohortSpec.simpleEvents?.attributes[newSimpleEventType]?.options?.filter(
      ({ filterType: attrFilterType }) => attrFilterType === filterType
    ) || [];
  if (
    ((filterType === SimpleEventFilterType.UNIQUENESS && attrOptions.length >= 1) ||
      attrOptions.length === 1) &&
    !attr.value
  ) {
    attr.value = attrOptions[0].value;
  }

  const attrOption = attrOptions.find((option) => option.value === attr.value);
  if (!edit && filterType === SimpleEventFilterType.RELATED_ENTITY && attrOption && entity) {
    entity.value = attrOption.entity;
  }

  // Determine operator options based on attribute valueType, or fall back to filter type defaults
  let opOptions = attrOption?.valueType
    ? cohortSpec.simpleEvents.operators[attrOption.valueType]
    : undefined;

  // If no options found (empty array or undefined), use filter type defaults
  if (!opOptions || opOptions.length === 0) {
    if (filterType === SimpleEventFilterType.NUMERIC) {
      // For numeric filters, use numeric operators (integer or float)
      opOptions = cohortSpec.simpleEvents.operators[ValueType.INTEGER] || [];
    } else if (filterType === SimpleEventFilterType.UNIQUENESS) {
      // For uniqueness filters, use days operators
      opOptions = cohortSpec.simpleEvents.operators[ValueType.DAYS] || [];
    } else {
      // Fallback to empty array if no filter type match
      opOptions = [];
    }
  }

  // Only set default operator value when creating a new filter (not editing) and no value is set
  if (!edit && opOptions?.length >= 1 && !op.value) {
    op.value = opOptions[0].value;
  }

  const remainingInputs = [unit, entity].filter(Boolean) as InputFieldLegacy[];
  const valueInputType = attrOption?.valueType
    ? VALUE_TYPE_TO_INPUT_TYPE[attrOption.valueType]
    : [SimpleEventFilterType.NUMERIC, SimpleEventFilterType.UNIQUENESS].includes(filterType)
    ? InputFieldTypeLegacy.INT_NUMERIC_FIELD
    : InputFieldTypeLegacy.COMBO_BOX;
  const valueLabel = attrOption?.valueType
    ? VALUE_TYPE_TO_INPUT_LABEL[attrOption.valueType]
    : SimpleEventFilterType.NUMERIC === filterType
    ? 'Number'
    : SimpleEventFilterType.UNIQUENESS === filterType
    ? 'Days'
    : 'Concept group';
  const valuePlaceholder =
    valueLabel === 'Concept group'
      ? 'Select concept group'
      : attrOption?.valueType === ValueType.FLOAT
      ? '0.000'
      : ['Days', 'Number'].includes(valueLabel)
      ? '0'
      : 'Value';

  return {
    [attr.id]: {
      inputType: InputFieldTypeLegacy.DROPDOWN,
      label: 'Attribute',
      options: attrOptions,
      placeholder: 'Select attribute',
      width: filterType === SimpleEventFilterType.NUMERIC ? 'small' : 'medium',
    },
    [op.id]: {
      inputType: InputFieldTypeLegacy.DROPDOWN,
      label: 'Operator',
      options: opOptions,
      placeholder: '',
      width: filterType === SimpleEventFilterType.UNIQUENESS ? 200 : 150,
    },
    [value.id]: {
      inputType: valueInputType,
      label: valueLabel,
      options:
        attr.value &&
        [SimpleEventFilterType.CONCEPT, SimpleEventFilterType.RELATED_ENTITY].includes(filterType)
          ? processConceptGroups(conceptGroups, attr.value as Attribute, newSimpleEventType)
          : [],
      placeholder: valuePlaceholder,
      width: valueLabel === 'Concept group' ? 'xxlarge' : 'small',
    },
    ...remainingInputs.reduce(
      (acc, input) => ({
        ...acc,
        [input.id]: {
          inputType: InputFieldTypeLegacy.TEXT_FIELD,
          label: filterType === SimpleEventFilterType.UNIQUENESS ? 'Days' : 'Entity',
          placeholder: `Select ${filterType === SimpleEventFilterType.UNIQUENESS ? '0' : 'entity'}`,
          width: 'auto',
        },
      }),
      {} as Record<string, InputFieldConfig>
    ),
  };
};

export function buildEventOptions(
  simpleEvents: SimpleEventLegacy[],
  derivedEvents: DerivedEventLegacy[] = [],
  eventFilterNames: string[] = [],
  currentEventName: string,
  currentEventType: DerivedEventTypeLegacy
): { value: string; displayValue: string }[] {
  // Helper function to get the correct type string for the option value
  const getTypeString = (eventType: SimpleEventType | SimpleEventTypeLegacy): string => {
    return eventType.toUpperCase();
  };

  const simpleOptions = simpleEvents.map((event) => {
    const typeString = event.type ? getTypeString(event.type) : '';
    return {
      value: `${typeString}::"${event.name}"`,
      displayValue: event.name,
    };
  });

  const derivedOptions = derivedEvents.map((event) => ({
    value: `${event.type ? event.type.toUpperCase() : ''}_EVENT::"${event.name}"`,
    displayValue: event.name,
  }));
  // Combine and filter out simple and derived events
  const options = [...simpleOptions, ...derivedOptions];
  return options.filter((option) => {
    const excludeCurrentEvent = option.displayValue !== currentEventName;
    const excludeEventsInUse =
      currentEventType !== DerivedEventTypeLegacy.SEQUENCE && eventFilterNames.length > 1
        ? !eventFilterNames.includes(option.value)
        : true;
    const excludeInstanceDuplicates =
      currentEventType === DerivedEventTypeLegacy.INSTANCE
        ? !option.value.startsWith('INSTANCE_EVENT::')
        : true;
    return excludeCurrentEvent && excludeEventsInUse && excludeInstanceDuplicates;
  });
}

export const buildConfigMapDerivedEvents = (
  derivedEvent: ActiveCohortBuilderItemLegacy,
  derivedFilter: DerivedEventFilterLegacy,
  simpleEvents: SimpleEventLegacy[],
  derivedEvents: DerivedEventLegacy[] = [],
  eventFilterNames: string[]
): Record<string, InputFieldConfig> => {
  const INDEX_UUID = uuid();

  const evnt = derivedFilter.eventName;
  const attr = derivedFilter.eventDateAttribute;
  const pos = derivedFilter.position;
  const selected = derivedFilter.isSelected;
  if (derivedFilter.index && !derivedFilter.index.id) {
    derivedFilter.index = { id: INDEX_UUID, value: '' } as InputFieldLegacy;
  }
  const index = derivedFilter.index;

  const events = buildEventOptions(
    simpleEvents,
    derivedEvents,
    eventFilterNames,
    derivedEvent.name,
    derivedEvent.type as DerivedEventTypeLegacy
  );
  const eventType = (derivedFilter.eventName.value ?? '')
    .split('::')[0]
    .toLowerCase() as SimpleEventType;

  const typeOptions = cohortSpec.derivedEvents.attributes[eventType] || [];
  if (typeOptions.length >= 1 && !attr.value) {
    attr.value = typeOptions[0].value;
  }

  const opOptions = (attr.value && cohortSpec.derivedEvents.attributes['position']) || [];
  if (opOptions.length >= 1 && pos && !pos.value) {
    pos.value = opOptions[0].value;
  }

  if (evnt.value && eventFilterNames.length > 1) {
    events.push({
      value: evnt.value,
      displayValue: evnt.value.split('::')[1]?.replace(/"/g, ''),
    });
  }

  const configMap: Record<string, InputFieldConfig> = {
    [evnt.id]: {
      inputType: InputFieldTypeLegacy.COMBO_BOX,
      label: 'Event',
      options: events,
      placeholder: 'Select event',
      width: COMPONENT_COMBOBOX_MEDIUM,
    },
    [attr.id]: {
      inputType: InputFieldTypeLegacy.DROPDOWN,
      label: 'Date attribute',
      options: typeOptions,
      placeholder: 'Select date attribute',
      width: COMPONENT_DROPDOWN_WIDE,
    },
  };

  if (pos) {
    configMap[pos.id] = {
      inputType: InputFieldTypeLegacy.DROPDOWN,
      label: 'Occurrence',
      options: opOptions,
      placeholder: '',
      width: COMPONENT_DROPDOWN_NARROW,
    };
    if (pos.value === 'INDEX' && index) {
      configMap[index.id] = {
        inputType: InputFieldTypeLegacy.INT_NUMERIC_FIELD,
        options: [{ value: 'INT', displayValue: '' }],
        label: 'Position',
        placeholder: '1',
        width: COMPONENT_NUMERIC_INTEGER,
        minValue: 1,
        minValueMsg: 'Min value is 1',
      };
    }
  }

  if (selected) {
    configMap[selected.id] = {
      inputType: InputFieldTypeLegacy.CHECKBOX,
      label: 'Use as event date',
      options: [],
      placeholder: '',
      width: COMPONENT_RADIO,
    };
  }

  return configMap;
};

export const buildConfigMapDerivedEventFilters = (
  filter: FilterLegacy,
  filterType: DerivedEventFilterType
): Record<string, InputFieldConfig> => {
  let attr;
  let [type, value, unit] = filter.inputFields;
  if (
    filterType === DerivedEventFilterType.UNIQUENESS ||
    filterType === DerivedEventFilterType.TEMPORAL
  ) {
    [type, value, unit, attr] = filter.inputFields;
    // Only set default values if they haven't been set and the objects are mutable
    if (!unit.value) {
      try {
        unit.value = UniquenessFilterTimeUnitEnumLegacy.DAYS;
      } catch {
        // Object is frozen, skip setting the value
      }
    }
    if (filterType === DerivedEventFilterType.UNIQUENESS && !attr.value) {
      try {
        attr.value = Attribute.DERIVED_EVENT_DATE;
      } catch {
        // Object is frozen, skip setting the value
      }
    }
  }

  const typeLabel =
    filterType === DerivedEventFilterType.UNIQUENESS ? 'Operator' : 'Constraint type';
  const typeOptions = cohortSpec.derivedEvents.attributes[filterType] || [];
  if (typeOptions.length >= 1 && !type.value) {
    type.value = typeOptions[0].value;
  }

  const typePlaceholder =
    filterType === DerivedEventFilterType.UNIQUENESS ? 'Select type' : 'Select constraint type';
  const typeWidth =
    filterType === DerivedEventFilterType.UNIQUENESS
      ? COMPONENT_COMBOBOX_NARROW
      : COMPONENT_COMBOBOX_MEDIUM;

  const remainingInputs = [attr].filter(Boolean) as InputFieldLegacy[];
  const configMap: Record<string, InputFieldConfig> = {
    [type.id]: {
      inputType: InputFieldTypeLegacy.DROPDOWN,
      label: typeLabel,
      options: typeOptions,
      placeholder: typePlaceholder,
      width: typeWidth,
    },
    [value.id]: {
      inputType: InputFieldTypeLegacy.INT_NUMERIC_FIELD,
      label: filterType === DerivedEventFilterType.UNIQUENESS ? 'Days' : 'Time value',
      options: [{ value: 'INT', displayValue: '' }],
      placeholder: '0',
      width: COMPONENT_NUMERIC_INTEGER,
    },
    [unit.id]: {
      inputType: InputFieldTypeLegacy.DROPDOWN,
      label: 'Time unit',
      options: [{ value: 'DAYS', displayValue: 'Days' }],
      placeholder: '',
      width: COMPONENT_DROPDOWN_NARROW,
    },
    ...remainingInputs.reduce(
      (acc, input) => ({
        ...acc,
        [input.id]: {
          inputType: InputFieldTypeLegacy.DROPDOWN,
          label: filterType === DerivedEventFilterType.UNIQUENESS ? 'Attribute' : 'Date attribute',
          options: [{ value: Attribute.DERIVED_EVENT_DATE, displayValue: 'Derived event date' }],
          placeholder: '',
          width: COMPONENT_DROPDOWN_WIDE,
        },
      }),
      {} as Record<string, InputFieldConfig>
    ),
  };
  return configMap;
};

export const buildSubjectVariableConfigMap = (
  filter: FilterLegacy,
  subjectVariableType: SubjectVariableTypeLegacy,
  filterKey: SubjectVariableFilterType,
  simpleEvents: SimpleEventLegacy[] = [],
  derivedEvents: DerivedEventLegacy[] = [],
  conceptGroups: ConceptGroup[] = []
): Record<string, InputFieldConfig> => {
  const [attr, op, value] = filter.inputFields;
  const { attributes, operators } = cohortSpec.subjectVariables;

  let attrOptions = attributes[filterKey]?.options || [];

  // For COUNT type, show all simple and derived events as options
  if (filterKey === SubjectVariableFilterType.COUNT) {
    attrOptions = buildEventOptions(
      simpleEvents,
      derivedEvents,
      [],
      '',
      '' as DerivedEventTypeLegacy
    );
  }

  // Auto-select the first option if there's exactly one option and no value is currently selected
  if (attrOptions.length === 1 && !attr?.value) {
    attr.value = attrOptions[0].value;
  }

  const attrDemographics =
    filterKey === SubjectVariableFilterType.CONCEPT ? 'Concept Attribute' : 'Attribute';
  const attrLabel =
    subjectVariableType === SubjectVariableTypeLegacy.DEMOGRAPHICS ? attrDemographics : 'Event';

  const opOptions = attr?.value ? operators[filterKey] : [];

  const valueInputType =
    filterKey === SubjectVariableFilterType.CONCEPT
      ? InputFieldTypeLegacy.CONCEPT_COMBO_BOX
      : InputFieldTypeLegacy.INT_NUMERIC_FIELD;
  let valueOptions: DropdownListItem[] = [];

  if (attr?.value && op?.value) {
    if (filterKey === SubjectVariableFilterType.CONCEPT) {
      valueOptions = processConceptGroups(
        conceptGroups,
        attr.value as Attribute,
        SimpleEventType.CONDITION_OCCURRENCE
      );
    } else {
      valueOptions = [{ value: 'INT', displayValue: '' }];
    }
  }

  const configMap: Record<string, InputFieldConfig> = {
    [attr.id]: {
      inputType:
        filterKey === SubjectVariableFilterType.COUNT
          ? InputFieldTypeLegacy.COMBO_BOX
          : InputFieldTypeLegacy.DROPDOWN,
      options: attrOptions,
      label: attrLabel,
      placeholder: `Select ${attrLabel.toLowerCase()}`,
      width:
        filterKey === SubjectVariableFilterType.COUNT
          ? COMPONENT_COMBOBOX_MEDIUM
          : COMPONENT_DROPDOWN_WIDE,
    },
  };

  // Add operator field if attribute has a value and operator field exists
  // For numeric/concept filters, operator should always be shown when attribute is set
  if (attr?.value && op) {
    configMap[op.id] = {
      inputType: InputFieldTypeLegacy.DROPDOWN,
      options: opOptions,
      label: 'Comparator',
      placeholder: '',
      width: COMPONENT_DROPDOWN_NARROW,
    };
  }

  // Add value field if attribute has a value, operator field exists, and value field exists
  // For numeric/concept filters, value should be shown when attribute is set and operator field exists
  // Note: We show the value field as soon as operator field exists (even without a value),
  // but we only populate valueOptions if both attr and op have values
  if (attr?.value && op && value) {
    configMap[value.id] = {
      inputType: valueInputType,
      options: valueOptions,
      label: filterKey === SubjectVariableFilterType.CONCEPT ? 'Concept group' : 'Value',
      placeholder:
        filterKey === SubjectVariableFilterType.CONCEPT ? 'Select concept attribute' : '0',
      width:
        filterKey === SubjectVariableFilterType.CONCEPT
          ? COMPONENT_COMBOBOX_WIDE
          : COMPONENT_NUMERIC_INTEGER,
    };
  }

  return configMap;
};

export const buildIndexDateConfigMap = (
  indexDateItem: ActiveCohortBuilderItemLegacy,
  simpleEvents: SimpleEventLegacy[] = [],
  derivedEvents: DerivedEventLegacy[] = []
): Record<string, InputFieldConfig> => {
  const configMap: Record<string, InputFieldConfig> = {};

  // Get the indexDate id
  const fieldId = indexDateItem.id;
  const eventOptions = buildEventOptions(
    simpleEvents,
    derivedEvents,
    [],
    '',
    '' as DerivedEventTypeLegacy
  )
    .filter((option) => option.displayValue)
    .filter((option) => {
      // Only include INSTANCE derived events
      return option.value.includes(GrammarDerivedEventTypeLegacy.INSTANCE);
    });

  configMap[fieldId] = {
    inputType: InputFieldTypeLegacy.COMBO_BOX,
    options: eventOptions,
    label: 'Event',
    placeholder: 'Select Event',
    width: 'auto',
  };

  if ((indexDateItem as ActiveIndexDateItemLegacy).indexDateEvent) {
    const { eventName, eventDateAttribute, position, occurrence } =
      (indexDateItem as ActiveIndexDateItemLegacy).indexDateEvent || {};

    if (eventName) {
      const eventOptions = buildEventOptions(
        simpleEvents,
        derivedEvents,
        [],
        '',
        '' as DerivedEventTypeLegacy
      )
        .filter((option) => option.displayValue)
        .filter((option) => {
          return !option.value.includes(GrammarDerivedEventTypeLegacy.INSTANCE);
        });

      configMap[eventName.id] = {
        inputType: InputFieldTypeLegacy.COMBO_BOX,
        label: 'Event',
        options: eventOptions,
        placeholder: 'Select event',
        width: 'auto',
      };
    }

    if (eventDateAttribute) {
      const eventType = (eventName?.value ?? '').split('::')[0].toLowerCase() as SimpleEventType;
      const typeOptions = cohortSpec.derivedEvents.attributes[eventType] || [];

      if (typeOptions.length >= 1 && !eventDateAttribute.value) {
        eventDateAttribute.value = typeOptions[0].value;
      }

      configMap[eventDateAttribute.id] = {
        inputType: InputFieldTypeLegacy.DROPDOWN,
        label: 'Date attribute',
        options: typeOptions,
        placeholder: 'Select date attribute',
        width: COMPONENT_DROPDOWN_WIDE,
      };
    }

    if (occurrence) {
      const opOptions = cohortSpec.indexDate.position || [];

      if (opOptions.length >= 1 && occurrence && !occurrence.value) {
        occurrence.value = opOptions[0].value;
      }

      configMap[occurrence.id] = {
        inputType: InputFieldTypeLegacy.DROPDOWN,
        label: 'Occurrence',
        options: opOptions,
        placeholder: '',
        width: COMPONENT_DROPDOWN_NARROW,
      };
    }

    if (position) {
      configMap[position.id] = {
        inputType: InputFieldTypeLegacy.INT_NUMERIC_FIELD,
        options: [{ value: 'INT', displayValue: '' }],
        label: 'Position',
        placeholder: '1',
        width: COMPONENT_NUMERIC_INTEGER,
        minValue: 1,
        minValueMsg: 'Min value is 1',
        helperText: 'Number >= 1',
      };
    }
  }

  return configMap;
};
